const API_BASE = '/api';

const fetchWithAuth = (input: RequestInfo | URL, init?: RequestInit) =>
  fetch(input, { credentials: 'include', ...init });

export interface Workout {
  id: number;
  name: string;
  sets: number;
  bodyParts: string[];
  day: number;
}

export interface TodaySet {
  id: number;
  sessionId: number;
  setNumber: number;
  weight: number;
  weightUnit: 'lbs' | 'kg';
  reps: number;
  completed: boolean;
}

export interface TodayWorkout {
  sessionId: number;
  workoutId: number;
  name: string;
  day: number;
  setsTarget: number;
  bodyParts: string[];
  sets: TodaySet[];
  totalPoints: number;
  completed: boolean;
}

export interface WorkoutDaysResponse {
  days: number[];
  daysInfo: { day: number; dayName: string }[];
}

export interface CreateWorkoutResponse extends Workout {
  existsOnDays: { day: number; dayName: string }[];
}

export interface CheckNameResponse {
  existsOnSameDay: boolean;
  existsOnDays: { day: number; dayName: string }[];
}

// Workouts
export const getWorkouts = async (day?: number): Promise<Workout[]> => {
  const url = day !== undefined
    ? `${API_BASE}/workouts?day=${day}`
    : `${API_BASE}/workouts`;
  const res = await fetchWithAuth(url);
  if (!res.ok) throw new Error('Failed to fetch workouts');
  return res.json();
};

export const createWorkout = async (
  workout: Omit<Workout, 'id'>
): Promise<CreateWorkoutResponse> => {
  const res = await fetchWithAuth(`${API_BASE}/workouts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(workout),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to create workout');
  }
  return res.json();
};

export const updateWorkout = async (
  id: number,
  workout: Omit<Workout, 'id'>
): Promise<Workout> => {
  const res = await fetchWithAuth(`${API_BASE}/workouts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(workout),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to update workout');
  }
  return res.json();
};

export const deleteWorkout = async (id: number): Promise<void> => {
  const res = await fetchWithAuth(`${API_BASE}/workouts/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete workout');
};

export const checkWorkoutName = async (
  name: string,
  day: number
): Promise<CheckNameResponse> => {
  const res = await fetchWithAuth(
    `${API_BASE}/workouts/check-name?name=${encodeURIComponent(name)}&day=${day}`
  );
  if (!res.ok) throw new Error('Failed to check workout name');
  return res.json();
};

export const getWorkoutDays = async (name: string): Promise<WorkoutDaysResponse> => {
  const res = await fetchWithAuth(
    `${API_BASE}/workouts/workout-days?name=${encodeURIComponent(name)}`
  );
  if (!res.ok) throw new Error('Failed to get workout days');
  return res.json();
};

// Rest days
export const getRestDays = async (): Promise<number[]> => {
  const res = await fetchWithAuth(`${API_BASE}/workouts/rest-days`);
  if (!res.ok) throw new Error('Failed to fetch rest days');
  return res.json();
};

export const setRestDay = async (
  day: number,
  isRest: boolean
): Promise<{ day: number; isRest: boolean }> => {
  const res = await fetchWithAuth(`${API_BASE}/workouts/rest-days`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ day, isRest }),
  });
  if (!res.ok) throw new Error('Failed to set rest day');
  return res.json();
};

// Today tab
export const getTodayWorkouts = async (date?: string): Promise<TodayWorkout[]> => {
  const url = date ? `${API_BASE}/today?date=${date}` : `${API_BASE}/today`;
  const res = await fetchWithAuth(url);
  if (!res.ok) throw new Error('Failed to fetch today\'s workouts');
  return res.json();
};

export const updateTodaySet = async (
  sessionId: number,
  setNumber: number,
  payload: Partial<Pick<TodaySet, 'weight' | 'weightUnit' | 'reps' | 'completed'>>
): Promise<TodayWorkout> => {
  const res = await fetchWithAuth(`${API_BASE}/today/sessions/${sessionId}/sets/${setNumber}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error('Failed to update set');
  return res.json();
};

export const deleteTodaySession = async (sessionId: number): Promise<void> => {
  const res = await fetchWithAuth(`${API_BASE}/today/sessions/${sessionId}`, {
    method: 'DELETE',
  });

  if (!res.ok) throw new Error('Failed to delete session');
};

// Results tab
export interface DailyTrendPoint {
  date: string;
  points: number;
  dayIndex: number;
}

export interface ExerciseTrend {
  name: string;
  bodyParts: string[];
  sessions: { date: string; points: number }[];
}

export interface BodyPartTrend {
  bodyPart: string;
  sessions: { date: string; points: number }[];
}

export const getDailyTrend = async (): Promise<DailyTrendPoint[]> => {
  const res = await fetchWithAuth(`${API_BASE}/results/daily`);
  if (!res.ok) throw new Error('Failed to fetch daily trend');
  return res.json();
};

export const getExerciseTrend = async (): Promise<ExerciseTrend[]> => {
  const res = await fetchWithAuth(`${API_BASE}/results/exercise`);
  if (!res.ok) throw new Error('Failed to fetch exercise trend');
  return res.json();
};

export const getBodyPartTrend = async (): Promise<BodyPartTrend[]> => {
  const res = await fetchWithAuth(`${API_BASE}/results/bodypart`);
  if (!res.ok) throw new Error('Failed to fetch body part trend');
  return res.json();
};
